import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';

const BookingModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+971',
    phone: '',
    service: '',
    date: '',
    time: '',
    description: ''
  });
  
  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  // Available services (from footer)
  const services = [
    { value: '', label: 'Select a Service' },
    { value: 'hair-care', label: 'Hair Care' },
    { value: 'beard-grooming', label: 'Beard Grooming' },
    { value: 'face-care', label: 'Face Care' },
    { value: 'relaxation', label: 'Relaxation & Massage' }
  ];

  // Available time slots (6:00 AM - 2:00 AM, excluding Friday 1-2 PM)
  const timeSlots = [
    { value: '', label: 'Select Time' },
    { value: '06:00', label: '06:00 AM' },
    { value: '07:00', label: '07:00 AM' },
    { value: '08:00', label: '08:00 AM' },
    { value: '09:00', label: '09:00 AM' },
    { value: '10:00', label: '10:00 AM' },
    { value: '11:00', label: '11:00 AM' },
    { value: '12:00', label: '12:00 PM' },
    { value: '13:00', label: '01:00 PM' },
    { value: '14:00', label: '02:00 PM' },
    { value: '15:00', label: '03:00 PM' },
    { value: '16:00', label: '04:00 PM' },
    { value: '17:00', label: '05:00 PM' },
    { value: '18:00', label: '06:00 PM' },
    { value: '19:00', label: '07:00 PM' },
    { value: '20:00', label: '08:00 PM' },
    { value: '21:00', label: '09:00 PM' },
    { value: '22:00', label: '10:00 PM' },
    { value: '23:00', label: '11:00 PM' },
    { value: '00:00', label: '12:00 AM' },
    { value: '01:00', label: '01:00 AM' },
    { value: '02:00', label: '02:00 AM' }
  ];

  // Get tomorrow's date as minimum selectable date
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Get max date (30 days from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30);
    return maxDate.toISOString().split('T')[0];
  };

  if (!isOpen) return null;

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address (e.g., user@example.com)';
    }
    return '';
  };

  const validatePhone = (phone) => {
    // Remove any non-digit characters for validation
    const cleanPhone = phone.replace(/\D/g, '');
    
    if (!phone) {
      return 'Phone number is required';
    }
    if (!/^\d+$/.test(phone)) {
      return 'Phone number can only contain digits';
    }
    if (cleanPhone.length !== 9) {
      return 'Phone number must be exactly 9 digits';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For phone field, only allow digits
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      // Limit to 9 digits
      const limitedValue = digitsOnly.slice(0, 9);
      setFormData({ ...formData, [name]: limitedValue });
      
      // Validate on change
      if (limitedValue) {
        const phoneError = validatePhone(limitedValue);
        setErrors({ ...errors, phone: phoneError });
      } else {
        setErrors({ ...errors, phone: '' });
      }
    } else if (name === 'email') {
      setFormData({ ...formData, [name]: value });
      
      // Validate email on change
      if (value) {
        const emailError = validateEmail(value);
        setErrors({ ...errors, email: emailError });
      } else {
        setErrors({ ...errors, email: '' });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields before submission
    const emailError = validateEmail(formData.email);
    const phoneError = validatePhone(formData.phone);
    
    setErrors({
      email: emailError,
      phone: phoneError
    });
    
    // If there are any errors, don't submit
    if (emailError || phoneError) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors before submitting.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      // Send to backend API
      const response = await fetch('/api/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit booking');
      }
      
      const result = await response.json();
      
      // Close modal
      onClose();
      
      // Navigate to Thank You page with booking data
      navigate('/thank-you', { 
        state: { bookingData: formData }
      });
      
      // Reset form
      setFormData({ 
        name: '', 
        email: '', 
        countryCode: '+971', 
        phone: '', 
        service: '',
        date: '',
        time: '',
        description: '' 
      });
      setErrors({ email: '', phone: '' });
      onClose();
      
    } catch (error) {
      console.error('Booking submission error:', error);
      toast({
        title: "Submission Failed",
        description: "Please try again or contact us directly.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-lg shadow-2xl w-full max-w-4xl flex overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ height: '600px' }}
      >
        {/* Left Side - Image */}
        <div className="hidden md:block w-1/2 relative">
          <img 
            src="https://images.unsplash.com/photo-1585747860715-2ba37e788b70?w=800&q=80"
            alt="Salon"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent flex items-center justify-center">
            <div className="text-center text-white px-8">
              <h3 className="text-3xl font-bold mb-2">LookWell Gents Salon</h3>
              <p className="text-lg">Book Your Appointment Today</p>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="w-full md:w-1/2 p-8 flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-black">Book Appointment</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-black transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-y-auto">
            <div className="space-y-4 flex-1 overflow-y-auto pr-2" style={{ maxHeight: '450px' }}>
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your full name"
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-amber-500 placeholder:text-gray-400"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                  className={`w-full px-4 py-3 border rounded focus:outline-none focus:border-amber-500 placeholder:text-gray-400 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              {/* Contact Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number *</label>
                <div className="flex gap-2">
                  <select
                    name="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    className="px-3 py-3 border border-gray-300 rounded focus:outline-none focus:border-amber-500 w-24"
                  >
                    <option value="+971">+971</option>
                    <option value="+1">+1</option>
                    <option value="+44">+44</option>
                    <option value="+91">+91</option>
                  </select>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="123 456 7890"
                    className={`flex-1 px-4 py-3 border rounded focus:outline-none focus:border-amber-500 placeholder:text-gray-400 ${
                      errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              {/* Service Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Service *
                </label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-amber-500"
                >
                  {services.map((service) => (
                    <option key={service.value} value={service.value}>
                      {service.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Preferred Date *
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  min={getTomorrowDate()}
                  max={getMaxDate()}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-amber-500"
                />
              </div>

              {/* Time Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Preferred Time *
                </label>
                <select
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-amber-500 appearance-auto"
                >
                  {timeSlots.map((slot) => (
                    <option key={slot.value} value={slot.value}>
                      {slot.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Additional Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Message</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-amber-500 resize-none"
                  placeholder="Tell us about the service you need..."
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-amber-500 text-black py-4 font-bold uppercase tracking-wider hover:bg-amber-600 hover:text-black transition-colors mt-6"
            >
              SUBMIT REQUEST
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
